/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useState, useCallback, useEffect } from 'react'
import { Checkbox, Alert, Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import BaseModal from '../ts-components/BaseModal'
import useNotificationPreferences from '@sportninja/common/hooks/useNotificationPreferences'
import { NOTIFICATION_CATEGORIES } from '@sportninja/common/constants/notificationCategories'
import Button from '../ts-components/Button'
import { NotificationChannelPreference } from '@sportninja/common/types/Notifications'

/**
 * Props for NotificationSettingsModal component
 */
interface NotificationSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Draft state for notification preferences (local changes before save)
 */
interface DraftPreferences {
  [category: string]: NotificationChannelPreference
}

/**
 * NotificationSettingsModal - Modal for managing notification preferences
 * Follows Figma design with table layout and save/cancel buttons
 */
const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  console.log('🔄 NotificationSettingsModal: Rendering modal')

  // API state management
  const {
    getChannelPreferences,
    updatePreference,
    isLoading,
    error: hookError,
    preferences,
  } = useNotificationPreferences()

  // Local draft state (changes before save)
  const [draftPreferences, setDraftPreferences] = useState<DraftPreferences>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  /**
   * Initialize draft preferences when modal opens or API preferences change
   */
  useEffect(() => {
    if (isOpen && !isLoading) {
      const draft: DraftPreferences = {}
      NOTIFICATION_CATEGORIES.forEach((category) => {
        const prefs = getChannelPreferences(category.value)

        // Force email to true for registration_payments
        if (category.value === 'registration_payments') {
          draft[category.value] = {
            ...prefs,
            email_enabled: true,
          }
        } else {
          draft[category.value] = prefs
        }
      })
      setDraftPreferences(draft)
      setSaveError(null)
    }
  }, [isOpen, isLoading, getChannelPreferences, preferences])

  /**
   * Handle checkbox toggle (local state only)
   */
  const handleCheckboxToggle = useCallback(
    (category: string, channel: 'push' | 'email' | 'sms', checked: boolean) => {
      console.log(
        '🔄 handleCheckboxToggle: Toggling',
        channel,
        'for',
        category,
        'to',
        checked
      )

      setDraftPreferences((prev) => ({
        ...prev,
        [category]: {
          ...prev[category],
          [`${channel}_enabled`]: checked,
        } as NotificationChannelPreference,
      }))
      setSaveError(null)
    },
    []
  )

  /**
   * Handle Save button click
   */
  const handleSave = useCallback(async () => {
    console.log('🔄 handleSave: Saving notification preferences')
    setIsSaving(true)
    setSaveError(null)

    try {
      // Update all changed preferences
      const updatePromises = NOTIFICATION_CATEGORIES.map(async (category) => {
        const draft = draftPreferences[category.value]
        const current = getChannelPreferences(category.value)

        // Force email_enabled to true for registration_payments
        const settingsToSave = {
          push_enabled: draft.push_enabled,
          email_enabled:
            category.value === 'registration_payments'
              ? true
              : draft.email_enabled,
          sms_enabled: draft.sms_enabled,
        }

        // Check if any values changed
        const hasChanges =
          settingsToSave.push_enabled !== current.push_enabled ||
          settingsToSave.email_enabled !== current.email_enabled ||
          settingsToSave.sms_enabled !== current.sms_enabled

        if (hasChanges) {
          return updatePreference(category.value, settingsToSave)
        }
        return true
      })

      const results = await Promise.all(updatePromises)

      // Check if all updates succeeded
      if (results.every((success) => success)) {
        console.log('🔄 handleSave: All preferences saved successfully')
        onClose()
      } else {
        throw new Error('Some preferences failed to save')
      }
    } catch (error: any) {
      console.error('🚨 handleSave: Error saving preferences:', error)
      setSaveError(error?.message || 'Failed to save notification preferences')
    } finally {
      setIsSaving(false)
    }
  }, [draftPreferences, getChannelPreferences, updatePreference, onClose])

  /**
   * Handle Cancel button click
   */
  const handleCancel = useCallback(() => {
    console.log('🔄 handleCancel: Canceling changes')
    onClose()
  }, [onClose])

  /**
   * Render table header row
   */
  const renderHeaderRow = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 8px 16px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 12px;
          line-height: 14.4px;
          letter-spacing: 1.44px;
          text-transform: uppercase;
          color: ${colors.NEUTRAL_50};
        `}
      >
        <div
          css={css`
            flex: 1;
            min-width: 0;
          `}
        >
          DESCRIPTION
        </div>
        <div
          css={css`
            width: 48px;
            text-align: center;
          `}
        >
          EMAIL
        </div>
        <div
          css={css`
            width: 48px;
            text-align: center;
          `}
        >
          PUSH
        </div>
        <div
          css={css`
            width: 48px;
            text-align: center;
            display: none;
          `}
        >
          SMS
        </div>
      </div>
    )
  }, [])

  /**
   * Render category row
   */
  const renderCategoryRow = useCallback(
    (category: (typeof NOTIFICATION_CATEGORIES)[0], index: number) => {
      const draft = draftPreferences[category.value]
      if (!draft) {
        return null
      }

      const isAlternate = index % 2 === 0

      return (
        <div
          key={category.value}
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 8px 16px;
            background: ${isAlternate ? colors.SECONDARY_400 : 'transparent'};
            min-height: 72px;
          `}
        >
          {/* Description Column */}
          <div
            css={css`
              flex: 1;
              min-width: 0;
              display: flex;
              flex-direction: column;
            `}
          >
            <div
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 500;
                font-size: 14px;
                line-height: 20.3px;
                color: ${colors.NEUTRAL_0};
                margin-bottom: 0;
              `}
            >
              {category.title}
            </div>
            <div
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 400;
                font-size: 12px;
                line-height: 17.4px;
                color: ${colors.NEUTRAL_50};
              `}
            >
              {category.description}
            </div>
          </div>

          {/* Email Checkbox - Conditional rendering based on category */}
          {(() => {
            // Hide email for team chat and in-game events
            if (
              category.value === 'chat' ||
              category.value === 'in_game_events'
            ) {
              return null
            }

            // Show disabled and always-on for registration_payments
            if (category.value === 'registration_payments') {
              return (
                <div
                  css={css`
                    width: 48px;
                    height: 48px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  `}
                >
                  <Checkbox
                    checked={true}
                    disabled={true}
                    css={css`
                      .ant-checkbox-inner {
                        border-radius: 4px;
                        border-color: ${colors.PRIMARY_300};
                      }
                      .ant-checkbox-checked .ant-checkbox-inner {
                        background-color: ${colors.PRIMARY_300};
                        border-color: ${colors.PRIMARY_300};
                      }
                      .ant-checkbox-disabled.ant-checkbox-checked
                        .ant-checkbox-inner {
                        background-color: ${colors.PRIMARY_300};
                        opacity: 0.6;
                      }
                    `}
                  />
                </div>
              )
            }

            // Normal behavior for other categories
            return (
              <div
                css={css`
                  width: 48px;
                  height: 48px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                `}
              >
                <Checkbox
                  checked={draft.email_enabled}
                  onChange={(e) =>
                    handleCheckboxToggle(
                      category.value,
                      'email',
                      e.target.checked
                    )
                  }
                  disabled={isSaving}
                  css={css`
                    .ant-checkbox-inner {
                      border-radius: 4px;
                      border-color: ${colors.PRIMARY_300};
                    }
                    .ant-checkbox-checked .ant-checkbox-inner {
                      background-color: ${colors.PRIMARY_300};
                      border-color: ${colors.PRIMARY_300};
                    }
                    .ant-checkbox-checked::after {
                      border-color: ${colors.PRIMARY_300};
                    }
                    &:hover .ant-checkbox-inner {
                      border-color: ${colors.PRIMARY_300};
                    }
                  `}
                />
              </div>
            )
          })()}

          {/* Push Checkbox */}
          <div
            css={css`
              width: 48px;
              height: 48px;
              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            <Checkbox
              checked={draft.push_enabled}
              onChange={(e) =>
                handleCheckboxToggle(category.value, 'push', e.target.checked)
              }
              disabled={isSaving}
              css={css`
                .ant-checkbox-inner {
                  border-radius: 4px;
                  border-color: ${colors.PRIMARY_300};
                }
                .ant-checkbox-checked .ant-checkbox-inner {
                  background-color: ${colors.PRIMARY_300};
                  border-color: ${colors.PRIMARY_300};
                }
                .ant-checkbox-checked::after {
                  border-color: ${colors.PRIMARY_300};
                }
                &:hover .ant-checkbox-inner {
                  border-color: ${colors.PRIMARY_300};
                }
              `}
            />
          </div>

          {/* SMS Checkbox */}
          <div
            css={css`
              width: 48px;
              height: 48px;
              display: flex;
              justify-content: center;
              align-items: center;
              display: none;
            `}
          >
            <Checkbox
              checked={draft.sms_enabled}
              onChange={(e) =>
                handleCheckboxToggle(category.value, 'sms', e.target.checked)
              }
              disabled={isSaving}
              css={css`
                .ant-checkbox-inner {
                  border-radius: 4px;
                  border-color: ${colors.PRIMARY_300};
                }
                .ant-checkbox-checked .ant-checkbox-inner {
                  background-color: ${colors.PRIMARY_300};
                  border-color: ${colors.PRIMARY_300};
                }
                .ant-checkbox-checked::after {
                  border-color: ${colors.PRIMARY_300};
                }
                &:hover .ant-checkbox-inner {
                  border-color: ${colors.PRIMARY_300};
                }
              `}
            />
          </div>
        </div>
      )
    },
    [draftPreferences, handleCheckboxToggle, isSaving]
  )

  /**
   * Render footer with Cancel and Save buttons
   */
  const renderFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 100%;
        `}
      >
        <Button
          text='CANCEL'
          onClick={handleCancel}
          variant='secondary'
          disabled={isSaving}
        />
        <Button
          text={isSaving ? 'SAVING...' : 'SAVE'}
          onClick={handleSave}
          variant='primary'
          disabled={isSaving}
        />
      </div>
    )
  }, [handleCancel, handleSave, isSaving])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title='Notification Settings'
      desktopWidth='800px'
      mobileWidth='90vw'
      actions={renderFooter()}
      removeBodyPaddingX={true}
    >
      {/* Error Alert */}
      {(hookError || saveError) && (
        <Alert
          message={hookError || saveError}
          type='error'
          showIcon
          closable
          onClose={() => setSaveError(null)}
          css={css`
            margin-bottom: 16px;
          `}
        />
      )}

      {/* Loading State */}
      {isLoading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 40px;
          `}
        >
          <Spin size='large' />
        </div>
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          {/* Table Header */}
          {renderHeaderRow()}

          {/* Category Rows */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {NOTIFICATION_CATEGORIES.map((category, index) =>
              renderCategoryRow(category, index)
            )}
          </div>
        </div>
      )}
    </BaseModal>
  )
}

export default NotificationSettingsModal
